import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Shows a mobile sticky "buy now" bar (with compact variant selects) and a
 * desktop "back to top" button, both of which appear once the main variant
 * picker (color/size options) has scrolled out of view above the viewport.
 *
 * The variant selects act as a remote control for the real variant picker —
 * changing one clicks the matching option there (so all of its existing
 * price/availability/form logic runs normally), and it reflects back the
 * other way by listening for the variant:update event.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} mobileBar
 * @property {HTMLButtonElement} toTopButton
 * @property {HTMLSelectElement[]} [optionSelects]
 *
 * @extends Component<Refs>
 */
export class StickyAddToCart extends Component {
  requiredRefs = ['mobileBar', 'toTopButton'];

  /** @type {HTMLElement | null} */
  #target = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#target = document.querySelector('variant-picker');
    if (this.#target) {
      // A plain scroll listener (rather than IntersectionObserver) so this is
      // correct even after a large instant scroll jump — e.g. a "jump to
      // reviews" anchor or pressing End — where the button can go straight
      // from below the viewport to above it without ever being observed as
      // intersecting.
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.#handleScroll();
    }

    for (const select of this.refs.optionSelects ?? []) {
      select.addEventListener('change', () => this.#applyOptionSelection(select));
    }

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      if (!this.#target) return;

      const scrolledPast = this.#target.getBoundingClientRect().top < 0;
      this.classList.toggle('sticky-add-to-cart--past', scrolledPast);
    });
  };

  /**
   * Applies a change made in one of the sticky bar's own selects to the real
   * variant picker elsewhere on the page, by clicking (buttons/swatches) or
   * setting-and-dispatching-change (dropdowns) its matching option.
   * @param {HTMLSelectElement} select
   */
  #applyOptionSelection(select) {
    const position = select.dataset.optionPosition;
    const value = select.value;
    const picker = document.querySelector('variant-picker');
    if (!picker || position === undefined) return;

    const radio = /** @type {HTMLInputElement | null} */ (
      picker.querySelector(`fieldset[data-fieldset-index="${position}"] input[value="${CSS.escape(value)}"]`)
    );

    if (radio) {
      radio.click();
      return;
    }

    const dropdown = picker.querySelectorAll('select.variant-option__select')[Number(position)];
    if (dropdown instanceof HTMLSelectElement) {
      dropdown.value = value;
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Keeps the sticky bar's selects in sync when the variant changes from the
   * real picker (or anywhere else).
   * @param {CustomEvent<{resource: Record<string, string>}>} event
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    (this.refs.optionSelects ?? []).forEach((select, index) => {
      const optionValue = variant[`option${index + 1}`];
      if (optionValue && select.value !== optionValue) select.value = optionValue;
    });
  };

  buyNow() {
    const button = /** @type {HTMLButtonElement | null} */ (document.querySelector('.buy-now-button__button'));
    button?.click();
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
